/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { compose, setDisplayName } from 'recompose';
import { Link } from 'react-router-dom';
import { withTracker } from 'components/Tracker';
import CssBaseline from '@material-ui/core/CssBaseline';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { BasicMain } from 'components';

const styles = theme => ({
  bottomSpacing: { marginBottom: theme.spacing.unit * 3 },
  bottomLeftSpacing: {
    marginBottom: theme.spacing.unit * 3,
    paddingLeft: theme.spacing.unit * 2,
  },
  bottomLeftLeftSpacing: {
    marginBottom: theme.spacing.unit * 3,
    paddingLeft: theme.spacing.unit * 6,
  },
});

const TermsOfService = ({ classes }) => (
  <BasicMain>
    <CssBaseline />
    <Typography className={classes.bottomSpacing} variant="h2" align="center" color="textPrimary">
      Terms of Service
    </Typography>
    <Typography className={classes.bottomSpacing} variant="subtitle2" color="textSecondary">
      Effective Date: <strong>January 1st, 2020</strong><br /><br />
      These terms of service ("Terms") apply to your access and use of&nbsp;
      <strong>
        Freshr App / Freshr Browser App / Freshr Online / Freshr Web Page (the "Service").
        Please read them carefully.
      </strong>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="subtitle2" color="textSecondary">
      Thank you for your interest in FRESHr web site at www.freshr.com (the “Site”) provided
      to you by Freshr Inc. (“FRESHr”, “us” or “we”), and our application for your mobile
      device (the "App"), as well as all related web sites, networks, and downloadable
      software provided by us and on which a link to these Terms of Service is displayed
      (collectively, together with the Site and App, our "Platform"). We may also
      provide you access to services identified as “Beta” or pre-release services.
      You understand that these services are still in development, may contain bugs or
      errors, may be feature incomplete, may materially change prior to a full
      commercial launch, or may never be released commercially. We provide
      Beta services AS IS, and without warranty of any kind, and your use of, or reliance on Beta
      services is at your own risk. These Terms of Service (the "Terms"),
      including the Privacy Policy incorporated into these Terms by reference
      and any other applicable policies and guidelines, as may be updated from time to time,
      govern your use of the Platform and Beta. These Terms constitute a legal
      agreement between you and FRESHr.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" align="center" color="textPrimary">
      IN ORDER TO USE THE PLATFORM YOU MUST AGREE TO THESE TERMS.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="subtitle2" color="textSecondary">
      All references to “you” or “your,” as applicable, mean the person who accesses,
      uses, and/or participates in the Platform in any manner, and any of your heirs,
      assigns, and successors. If you use the Platform on behalf of an entity,
      organization, or company, you represent and warrant that you have the authority to
      bind that entity to these Terms, your acceptance of the Terms will be deemed
      an acceptance by that entity, and “you” and “your” herein shall refer to that entity.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      BY DOWNLOADING, INSTALLING, OR OTHERWISE ACCESSING OR USING THE PLATFORM,
      YOU AGREE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE
      TERMS. IF YOU DO NOT AGREE, YOU MAY NOT USE THE PLATFORM.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      1. FRESHr PLATFORM
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform connects restaurants, grocers, and other buyers (“Buyers”) with farms (“Sellers”)
      and distributors (”Distributors”) to facilitate online ordering of produce and merchandise
      (“Products”) and delivery services. Through the Platform, Buyers may   order Products
      and request that they be made available for pick-up or delivered to them from a Seller
      by Distributor or by Seller itself.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} color="textSecondary" variant="subtitle2">
      FRESHr is not a farm, distributor, restaurant, store, delivery platform. FRESHr
      is not liable or responsible for Buyers’, Sellers’ and Distributors’ compliance
      with applicable federal, state, or local laws, rules, regulations or standards
      pertaining to their businesses. In addition, FRESHr does not guarantee the quality
      of what Sellers and/or Distributors sell and deliver, does not independently verify,
      and is not liable for, representations made by Sellers and/or Distributors
      regarding their products and services on the Platform.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} color="textSecondary" variant="subtitle2">
      FRESHr does not provide delivery services. Sellers and Distributors are separate entities and
      are not employees, partners, agents, joint ventures, or franchisees of FRESHr. Sellers and/or
      Distributors on behalf of Sellers or on Distributors’ own behalf  have entered into agreements
      with Buyers in order to sell and/or deliver Products to the latter. Sellers and Distributors have
      entered into agreements between themselves in order to sell and/or deliver products. FRESHr shall
      not be liable or responsible for any delivery services provided by Sellers and/or Distributors, or
      any errors or misrepresentations made by any of them. You hereby acknowledge that FRESHr does not
      supervise, direct, control, or monitor Seller’s and/or Distributor’s provision of services and expressly
      disclaims any responsibility or liability for the services performed.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} color="textSecondary" variant="subtitle2">
      <p>As provided in greater detail in these Terms, you agree and acknowledge these material terms:</p>
      <ul>
        <li>
        The Platform is licensed, not sold to you, and you may use the Platform only as set forth in these Terms;
        </li>
        <li>
          Your use of the Platform may be subject to separate third party terms of service and fees, including without
          limitation your mobile network operator’s ("Carrier") terms of service and fees, including fees charged for
          data usage and overage, which are your sole responsibility;
        </li>
        <li>
          You consent to the collection, sharing, and use of your personally identifiable information in accordance with
          FRESHr’s <Link to="privacy">Privacy Policy</Link>;
        </li>
        <li>
          Alcohol listings on FRESHr are intended for adults. You must be at least 21 years of age to purchase alcohol,
          or use any site functionality related to alcohol. FRESHr reserves the right to refuse service, terminate accounts,
          terminate your rights to use the Platform, remove or edit content, or cancel orders in its sole discretion;
        </li>
        <li>
          You may not use the Services if you are under 13 years of age;
        </li>
        <li>
          The following special requirements apply in relation to persons that are not at least 18 years old. If you are
          an individual or sole proprietor, and you are not at least 18 years old, but you are 13 years old or older,
          your Representative must be your parent or legal guardian. If you are a legal entity that is owned, directly
          or indirectly, by an individual who is not at least 18 years old, but the individual is 13 years old or older,
          your Representative must either obtain the consent of your board or of an authorized officer. Any such approving
          board, authorized officer, parent, or legal guardian is responsible to FRESHr and is legally bound to this Agreement
          as if it had agreed to the terms of this Agreement itself.
        </li>
        <li>
          The Platform is provided "as is" without warranties of any kind, and FRESHr’s liability to you is limited; and
        </li>
        <li>
          Disputes arising under these Terms will be resolved by binding individual arbitration.
        </li>
      </ul>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      2. ACCOUNTS AND REGISTRATION
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      To access some features of the Platform, you must register for an account. When you register for an account,
      you may be required to provide us with some information about yourself (such as your name, credit card or bank
      account information, e-mail address, phone number, profile image, or other contact information). You may be required
      to be logged in to the account and have a valid payment method associated with it. If there is a problem charging
      your selected payment method, we may charge any other valid payment method associated with your account. You agree
      that the information you provide to us is accurate, current, and complete, and that you will keep it up-to-date at all times.
      When you register, you will be asked to provide a password. You are solely responsible for maintaining the confidentiality
      of your account and password, and any password for other third-party login. You accept responsibility for all activities
      that occur under your account. If you have reason to believe that your account is no longer secure, you must immediately
      notify us via our Help Center. FRESHr will not be liable and you may be liable for losses, damages, liability, expenses,
      and lawyers’ fees incurred by FRESHr or a third party arising from someone else using your account due to your conduct
      regardless of whether you have notified us of such unauthorized use. You understand and agree that we may require you to
      provide information that may be used to confirm your identity and help ensure the security of your account.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      3. SCOPE OF LICENSE
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform is licensed, not sold, to you for use only under the terms of this license. FRESHr reserves all rights not
      expressly granted to you. Subject to your complete and ongoing compliance with these Terms, FRESHr hereby grants you a
      personal, limited, non-sublicensable, non-transferable, and revocable license to access the Platform on compatible devices
      that you own or control, and only in a manner that complies with all legal requirements that apply to you or your use of
      the Platform. FRESHr may revoke this license at any time, in its sole discretion.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      You may not modify, alter, reproduce, distribute the Platform. You may not rent, lease, lend, sell, redistribute
      or sublicense the Platform. If you breach these license restrictions, or otherwise exceed the scope of the license
      granted in these Terms, you may be subject to prosecution and legal damages, as well as liability for infringement of
      intellectual property rights. These Terms will govern any updates provided to you by FRESHr that replace and/or
      supplement the original software or App, unless the upgrade is accompanied by a separate license in which case the
      terms of that license will govern.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      4. COPYRIGHT, TRADEMARKS AND PATENTS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      All content included in or made available through the Platform, such as text, graphics, logos,
      button icons, images, audio clips, digital downloads, data compilations, and software is the
      property of FRESHr or its content suppliers and protected by United States and international
      copyright laws. The compilation of all content included in or made available through the
      Platform is the exclusive property of FRESHr and protected by U.S. and international copyright laws.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      In addition, graphics, logos, page headers, button icons, scripts, and service names included in
      or made available through the Platform are trademarks or trade dress of FRESHr in the U.S. and other
      countries. FRESHr's trademarks and trade dress may not be used in connection with any product or service
      that is not FRESHr's, in any manner that is likely to cause confusion among customers, or in any manner
      that disparages or discredits FRESHr. All other trademarks not owned by FRESHr that appear on the
      Platform are the property of their respective owners, who may or may not be affiliated with, connected
      to, or sponsored by FRESHr.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      5. PAYMENT TERMS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.1. FRESHr FEE
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      Certain features of the Platform, including placing orders, may require you to pay fees
      to FRESHr. FRESHr may change, or add fees for use of our Platform at any time as we deem
      necessary or appropriate for our business. In all cases, you acknowledge and accept that a
      fee will be charged and you agree to pay said fee. We encourage you to check our Site or visit
      our Help Center periodically to learn more about how we charge for the Platform.
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      FRESHr will charge the payment method you specify at the time of purchase or as otherwise
      specified by you in your account information. We may also place a pre-authorization hold on
      your payment method, as described in Section 5.2.
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      FRESHr has no obligation to provide refunds or credits. However, FRESHr, in its sole discretion,
      may provide users with refunds, or make promotional offers with different features and different
      rates to any users. Such offers are non-transferrable, may not be resold, and are not redeemable
      for cash or other considerations. FRESHr reserves its right to expire, limit, or modify any promotion
      at any time. If your account is cancelled for any or no reason, you may forfeit any pending, current,
      or future promotional offers and any other forms of unredeemed value in or associated with your account
      without prior notice to you.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.2. PAYMENT AUTHORIZATION
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        When you are paying via FRESHr payment system (“Payment System”) you authorize FRESHr to
        charge all sums for orders that you make to the payment method designated in your account.
      </p>
      <p>
        When you place an order through FRESHr, a temporary pre-authorization hold maybe placed on
        your designated payment method to verify that the payment method is valid and has funds
        available for your intended purchase. Your payment will be captured up to 24 hours after
        your order is completed. In the event that the pre-authorization is greater than the final
        amount, the difference will be refunded after your order is completed or cancelled.
        Depending on your bank, it may take up to 7 business days for you to receive access to these refund amounts.
      </p>
      <p>
        FRESHr also places an initial temporary pre-authorization hold on each new payment method you add to your account.
      </p>
      <p>
        FRESHr reserves the right to request additional information from you if we have reason to believe, in our sole discretion, that a payment method may be fraudulent.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.3. DELINQUENT ACCOUNTS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        If payment due on your account is delinquent, we reserve the right to suspend or terminate your
        access to the Platform; your account information may be sent to a collection agency/debt collector
        and you may be subject to a collection action, and payment of collection related fees and costs.
        If you have questions regarding an outstanding balance on your account, please contact us via our <a target="_blank" href="https://freshrsupport.zendesk.com/hc/en-us">Help Center</a>.
      </p>
      <p>
        If your payment card expires or is replaced by your issuing bank, the card network may provide us
        with updated card details associated with the same account. We may use these new details in order
        to help prevent any interruption to the Platform. If you would like to use a different payment method
        or remove a saved payment method from your account, please visit your account settings to update your bank information.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.4. BUYER NOT AVAILABLE
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      FRESHr reserves the right to charge you the full order amount, including any FRESHr fees,
      if you are not at the designated delivery location when the Seller or Distributor arrives to
      complete the delivery or if you fail to pick up an order from a Seller or Distributor.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.5. CANCELLED ORDERS AND REFUNDS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      We encourage you to check our Site or visit our <a target="_blank" href="https://freshrsupport.zendesk.com/hc/en-us">Help Center</a> periodically to learn more about how we deal with cancelled orders and refunds.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      5.6. TAXES
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      Our fees are exclusive of any applicable Taxes, except as expressly stated to the contrary.
      You have sole responsibility and liability for: (i) determining what, if any, Taxes apply to
      the sale of your products and services, or payments you receive in connection with your use of the
      Platform; and (ii) assessing, collecting, reporting, and remitting Taxes for your business to the
      appropriate tax and revenue authorities.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      6. THIRD-PARTY INTERACTIONS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      6.1. SELLERS AND DISTRIBUTORS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform enables you to engage third-party Seller and/or Distributor to provide ordering
      and delivery services. Any interactions or disputes between you and a Seller and/or Distributor
      are solely between you and that Seller and/or Distributor. FRESHr and its licensors shall have no
      liability, obligation or responsibility for any interaction between you and any Seller and/or Distributor.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      6.2. THIRD-PARTY ADVERTISING
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform may contain third-party advertising and marketing. By agreeing to these Terms you agree to receive such advertising and marketing.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      6.3. LINKS TO THIRD PARTY SITES
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform may contain links to third-party websites. Linked websites are not under
      our control, and we are not responsible for their content.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      7. YOUR USE OF PLATFORM
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      In order to use the Platform, you agree to the following:
      <ul>
        <li>
          The Platform, including any content that forms part of the Platform, contains proprietary
          content, information and material that is protected by applicable intellectual property
          and other laws, including copyright;
        </li>
        <li>
          You will not violate, or encourage others to violate, any right of a third party,
          including by infringing or misappropriating any third-party intellectual property
          rights, or otherwise engage in the unauthorized use of any proprietary content,
          information or materials;
        </li>
        <li>
          Unless expressly permitted, you may not alter, modify, create derivative works of,
          sell, license or in any way exploit any part of the Platform, and you agree not to copy,
          reproduce, distribute, publish, display, perform, transmit, stream or broadcast any
          part of the Platform without prior written authorization;
        </li>
        <li>
          You agree not to bypass any security or other features of the Platform designed to
          control the manner in which the Platform is used, harvest or mine content from the
          Platform, or otherwise access or use the Platform in a manner inconsistent with individual human use;
        </li>
        <li>
          You agree not to perform any fraudulent activity, including impersonating any person or entity, or
          accessing any other Platform account without permission;
        </li>
        <li>
          You agree not to decipher, reverse engineer, decompile or disassemble the Platform, or the software
          used to provide the Platform, in whole or in part, or authorize, direct, or cause a third party to do so;
        </li>
        <li>
        You agree not to use, display, mirror, frame or utilize framing techniques to enclose the Platform,
        or any portion thereof, unless and solely to the extent FRESHr makes available the means for embedding
        any part of the Platform;
        </li>
        <li>
          You agree not to access, tamper with, or use non-public areas of the Platform, FRESHr’s (and its hosting company’s)
          computer systems and infrastructure, or the technical delivery systems of FRESHr’s providers;
        </li>
        <li>
          You agree not to use any robot, spambot, spider, crawler, scraper or other automated means
          or interface not provided by us to access the Platform or to extract data;
        </li>
        <li>
          You agree not to use the Platform in any manner to harass, abuse, stalk, threaten, defame
          or otherwise infringe or violate the rights of any other party, and that FRESHr is not in any
          way responsible for any such use by you, nor for any harassing, threatening, defamatory, offensive
          or illegal messages or transmissions that you may receive as a result of using the Platform;
        </li>
        <li>
          You agree that your use of the Platform may incur third-party fees, such as fees charged by your
          Carrier for data usage, and may be subject to third-party terms, such as your Carrier’s terms of
          service, and you agree to pay all such fees and abide by all such terms; and
        </li>
        <li>
          You agree not to introduce viruses, time-bombs, worms, cancelbots, trojan horses
          and/or other harmful code into the Platform.
        </li>
      </ul>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      8. CONSENT TO USE OF DATA
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      Please see FRESHr’s <Link to="/privacy">Privacy Policy</Link>, for more information regarding information FRESHr
      collects, and how it uses and shares that information.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      9. INTELLECTUAL PROPERTY OWNERSHIP
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      The Platform, and the media and materials contained in the Platform, including all intellectual
      property rights in the Platform, are the sole and exclusive property of FRESHr. Except for the
      limited license expressly granted to you under these Terms, no other rights, licenses, or immunities
      are granted or shall be deemed to be granted under these Terms, either expressly, or by implication,
      estoppel or otherwise. All rights not expressly granted by FRESHr in these Terms are expressly reserved.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      10. CONTENT YOU PROVIDE THROUGH THE PLATFORM
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      10.1. CONTENT YOU PROVIDE
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        You may be able to submit, post, upload, denote, or otherwise make available
        (collectively, “Post”) photographs, favorites, liked items, bookmarked Buyers, Sellers and/or
        Distributors, personal information, store inventory, questions, comments, messages, ideas, designs,
        concepts, inventions, feedback, and other content (collectively, “User Content”) so long as the content
        is not illegal, obscene, threatening, defamatory, invasive of privacy, infringing of intellectual
        property rights (including publicity rights), or otherwise injurious to third parties or objectionable,
        and does not consist of or contain software viruses, political campaigning, commercial solicitation, chain
        letters, mass mailings, or any form of "spam" or unsolicited commercial electronic messages. User content may
        or may not be viewable by other users through the Platform or through other communications with you, including,
        without limitation, through text (“SMS”), chat boxes, or multimedia (“MMS”) messages (“Interactive Areas”).
      </p>
      <p>
        FRESHr reserves the right (but not the obligation) to remove or edit such content, but does not regularly review posted content.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      10.2. USE OF YOUR CONTENT
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      You acknowledge that when you Post User Content on or through the Platform, the User Content shall
      be deemed, and shall remain FRESHr’s property. FRESHr does not provide any compensation for User Content
      that you Post on or through the Platform. You agree that User Content that you Post on or through the
      Platform shall be deemed to be non-confidential and non-proprietary, and FRESHr shall have no obligation
      of any kind with respect to such information. When you Post User Content on or through the Platform, you
      grant FRESHr a world-wide, perpetual, irrevocable, royalty-free, non-exclusive, and sub-licensable license
      to use, copy, distribute, reproduce, modify, edit, adapt, publicly perform, publicly display, translate,
      create derivative works from, sell, lease, transmit, communicate to the public, disassemble, and publish
      such User Content in connection with FRESHr’s or its partners’ or affiliates’ business, in whole or in part,
      in any format or medium now known or developed in the future, for any purpose whatsoever, unless expressly
      agreed otherwise in writing by FRESHr, including without limitation for promoting and redistributing part or
      all of the Platform (and derivative works thereof) in any media formats and through any media channels.
      You further grant FRESHr a license to use your username and/or other user profile information, including,
      without limitation, your ratings history and how long you have been a FRESHr user, to attribute User Content to
      you, if we choose to do so. Further, FRESHr shall be free to use any ideas, concepts, know-how or techniques
      contained in User Content for any purpose whatsoever in connection with our business or the business of our
      partners or affiliates, without notice to, approval by, or compensation to you, including but not limited to
      developing, manufacturing and marketing products incorporating such information. In the interest of clarity, the
      license granted to FRESHr shall survive termination of the Platform or your account. Notwithstanding the foregoing,
      our use of your personal data shall be governed by the FRESHr <Link to="/privacy">Privacy Policy</Link>.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      10.3. USER CONTENT REPRESENTATIONS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      You acknowledge and agree that all User Content that you Post is your sole responsibility.
      You represent that you have all required rights to Post or transmit such User Content without
      violation of any third-party rights. You understand that FRESHr does not control, and is not responsible
      for, User Content, and that by using the Platform, you may be exposed to User Content from other users
      that is offensive, indecent, inaccurate, misleading, or otherwise objectionable. Please also note that
      User Content may contain typographical errors, other inadvertent errors or inaccuracies. You agree that
      you will indemnify, defend, and hold harmless FRESHr for all claims resulting from User Content you Post.
      FRESHr  has the right but not the obligation to monitor and edit or remove any activity or content. FRESHr
      takes no responsibility and assumes no liability for any content posted by you or any third party.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      10.4. USER CONTENT REVIEW
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      You acknowledge and agree that FRESHr and its designees may or may not (but do not assume any obligation to),
      at FRESHr’s discretion, pre-screen User Content before its appearance on the Platform. You further acknowledge
      and agree that FRESHr reserves the right (but does not assume the obligation) in its sole discretion to reject,
      move, edit or remove any User Content that is contributed to the Platform. Additionally, FRESHr has the right to
      remove any User Content that violates these Terms or is otherwise objectionable in FRESHr’s sole discretion.
      You acknowledge and agree that FRESHr does not verify, adopt, ratify, or sanction User Content, and you agree
      that you must evaluate and bear all risks associated with your use of User Content or your reliance on the accuracy,
      completeness, or usefulness of User Content.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      11. COMMUNICATIONS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      11.1. EMAIL
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      FRESHr may send you operational emails about your use of the Platform, as well as marketing or other
      promotional messages. You may opt-out of promotional emails by following the unsubscribe instructions
      in a promotional email. You will not be able to use the Platform without agreeing to receive operational emails.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      11.2. PUSH NOTIFICATIONS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      When you install our App on your mobile device you agree to receive push notifications, which
      are messages an app sends you on your mobile device even when the app is not on. You can turn off
      notifications by visiting your mobile device's "settings" page.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      12. INDEMNITY
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      You are responsible for your use of the Platform, and you agree to defend (at FRESHr’s option),
      indemnify, and hold harmless FRESHr and its officers, directors, employees, contractors, consultants,
      affiliates, investors, service providers, business partners, subsidiaries and agents from and against
      every claim, liability, damage, loss, and expenses, including reasonable attorneys’ fees and costs,
      arising out of or in any way connected with:
      <ul className="mt-3">
        <li>
          Your violation of any of these Terms, any representation, warranty, or agreement referenced in these
          Terms, or any applicable law or regulation;
        </li>
        <li>
          Your violation of any third-party right(s), including any intellectual property right(s) or publicity,
          confidentiality, other property, or privacy right; or
        </li>
        <li>
          Any dispute or issue between you and any third party, including any Buyer, Seller, Distributor, or other third-party.
        </li>
      </ul>
      FRESHr reserves the right, at our own expense, to assume the exclusive defense and control of any matter otherwise
      subject to indemnification by you (without limiting your indemnification obligations) and you agree to cooperate with
      our defense of that claim. If the defense or settlement is assumed by you, FRESHr may at any time thereafter elect to
      take over control of the defense and settlement of the claim. You must not settle any claim without FRESHr’s prior written consent.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      13. DISCLAIMER OF WARRANTIES
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        YOUR USE OF THE PLATFORM IS AT YOUR SOLE RISK. THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE"
        WITHOUT WARRANTIES OF ANY KIND EITHER EXPRESS OR IMPLIED. ALL REPRESENTATIONS AND WARRANTIES, WHETHER EXPRESS,
        IMPLIED, STATUTORY OR OTHERWISE, INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF THIRD-PARTY RIGHTS, ARE HEREBY DISCLAIMED BY FRESHR TO THE MAXIMUM EXTENT
        PERMITTED BY APPLICABLE LAW. FRESHR MAKES NO REPRESENTATION, WARRANTY, OR GUARANTEE AS TO THE RELIABILITY, TIMELINESS,
        QUALITY, SUITABILITY, AVAILABILITY, ACCURACY OR COMPLETENESS OF THE PLATFORM. FRESHR DOES NOT REPRESENT OR WARRANT
        THAT THE USE OF THE PLATFORM WILL BE SECURE, TIMELY, UNINTERRUPTED OR ERROR-FREE OR WILL OPERATE IN COMBINATION WITH ANY
        OTHER HARDWARE, SOFTWARE, SYSTEM OR DATA; THAT THE PLATFORM OR ANY PRODUCTS OR MATERIALS OBTAINED BY YOU THROUGH THE
        PLATFORM WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS; OR THAT THE PLATFORM OR THE SERVER(S) THAT MAKE THE PLATFORM
        AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
      </p>
      <p>
        FRESHR’S PLATFORM MAY BE SUBJECT TO LIMITATIONS, DELAYS, AND OTHER PROBLEMS INHERENT IN THE USE OF THE INTERNET
        AND ELECTRONIC COMMUNICATIONS. FRESHR IS NOT RESPONSIBLE FOR ANY DELAYS, DELIVERY FAILURES, OR OTHER DAMAGE
        RESULTING FROM SUCH PROBLEMS.
      </p>
      <p>
        FRESHR RELIES UPON MERCHANTS, INCLUDING, BUT NOT LIMITED TO, SELLERS, DISTRIBUTORS, RESTAURANTS,
        GROCERS, AND OTHER THIRD-PARTY FOOD AND BEVERAGE PROVIDERS AND RETAILERS TO PROVIDE ACCURATE ALLERGEN
        AND DIETARY INFORMATION AND GENERAL PRODUCT SAFETY. FRESHR DOES NOT REPRESENT OR WARRANT THAT SUCH INFORMATION
        ACCESSIBLE THROUGH THE PLATFORM IS ACCURATE, COMPLETE, RELIABLE, CURRENT OR ERROR-FREE, INCLUDING WITHOUT
        LIMITATION MENUS, NUTRITIONAL AND ALLERGEN INFORMATION, PHOTOS, FOOD QUALITY OR DESCRIPTIONS, PRICING, HOURS OF
        OPERATION, OR REVIEWS. ALL CONTENT IS PROVIDED FOR INFORMATIONAL PURPOSES ONLY. RELIANCE ON ANY INFORMATION
        PROVIDED THROUGH THE PLATFORM IS SOLELY AT YOUR OWN RISK, INCLUDING WITHOUT LIMITATION NUTRITIONAL AND ALLERGEN INFORMATION.
      </p>
      <p>
        FRESHR DOES NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR PLATFORM ADVERTISED
        OR OFFERED BY A SELLER AND/OR DISTRIBUTOR OR OTHER THIRD PARTY THROUGH THE PLATFORM OR ANY HYPERLINKED WEBSITE
        OR THIRD-PARTY SERVICE, AND FRESHR WILL NOT BE A PARTY TO OR IN ANY WAY MONITOR ANY TRANSACTION BETWEEN YOU AND
        THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES.
      </p>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      14. LIMITATION OF LIABILITY
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        IN NO EVENT SHALL FRESHR’S AGGREGATE LIABILITY EXCEED THE FRESHR FEES ACTUALLY PAID BY YOU
        TO FRESHR IN THE THREE (3) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
        IN NO EVENT SHALL FRESHR AND/OR ITS LICENSORS BE LIABLE TO ANYONE FOR ANY INDIRECT, PUNITIVE,
        SPECIAL, EXEMPLARY, INCIDENTAL, CONSEQUENTIAL OR OTHER DAMAGES OF ANY TYPE OR KIND (INCLUDING
        PERSONAL INJURY, LOSS OF DATA, REVENUE, PROFITS, REPUTATION, USE OR OTHER ECONOMIC ADVANTAGE)
        EVEN IF FRESHR AND/OR ITS LICENSORS HAVE BEEN PREVIOUSLY ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        THIS LIMITATION OF LIABILITY SECTION APPLIES FULLY IN ALL STATES.
      </p>
      <p>
        THE PLATFORM CONNECTS BUYERS WITH SELLERS AND/OR DISTRIBUTORS FOR THE PURPOSES OF FACILITATING
        DELIVERY AND PRODUCT FULFILLMENT SERVICES. FRESHR WILL NOT ASSESS THE SUITABILITY, LEGALITY OR
        ABILITY OF ANY BUYERS, SELLERS AND DISTRIBUTORS, AND YOU EXPRESSLY WAIVE AND RELEASE FRESHR FROM
        ANY AND ALL LIABILITY, CLAIMS OR DAMAGES ARISING FROM OR IN ANY WAY RELATED TO THE BUYERS, SELLERS
        OR DISTRIBUTORS. FRESHR WILL NOT ASSESS THE QUALITY, SAFETY, OR LEGALITY OF THE ITEMS PROVIDED BY
        SELLER AND/OR DISTRIBUTOR ON THE PLATFORM, AND YOU EXPRESSLY WAIVE AND RELEASE FRESHR FROM ANY AND
        ALL LIABILITY, CLAIMS OR DAMAGES ARISING FROM OR IN ANY WAY RELATED TO SELLERS AND/OR DISTRIBUTORS,
        AND THE ITEMS THEY PROVIDE. FRESHR WILL NOT BE A PARTY TO DISPUTES OR NEGOTIATIONS OF DISPUTES,
        BETWEEN YOU AND ANY BUYERS, SELLERS AND/OR DISTRIBUTORS. RESPONSIBILITY FOR THE DECISIONS YOU MAKE
        REGARDING SERVICES OFFERED VIA THE PLATFORM (WITH ALL THEIR IMPLICATIONS) RESTS SOLELY WITH YOU. IN
        ADDITION, WE WILL NOT ASSESS THE SUITABILITY, LEGALITY OR ABILITY OF ANY THIRD PARTIES, AND YOU EXPRESSLY
        WAIVE AND RELEASE FRESHR FROM ANY AND ALL LIABILITY, CLAIMS, CAUSES OF ACTION, OR DAMAGES ARISING FROM
        YOUR USE OF THE PLATFORM, OR IN ANY WAY RELATED TO THE THIRD PARTIES INTRODUCED TO YOU BY THE PLATFORM.
        IF YOU ARE A CALIFORNIA RESIDENT, YOU WAIVE CALIFORNIA CIVIL CODE §1542, WHICH SAYS:
      </p>
      <p>
        A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR SUSPECT TO EXIST IN
        HIS OR HER FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH IF KNOWN BY HIM OR HER MUST HAVE MATERIALLY
        AFFECTED HIS OR HER SETTLEMENT WITH THE DEBTOR.
      </p>
      <p>
        THE QUALITY OF THE SELLER AND/OR DISTRIBUTOR PROVIDING SERVICES THROUGH THE USE OF THE PLATFORM IS
        ENTIRELY THE RESPONSIBILITY OF THE THIRD-PARTY SELLER AND/OR DISTRIBUTOR WHO ULTIMATELY PROVIDES DELIVERY
        SERVICES TO YOU. YOU UNDERSTAND THAT BY USING THE PLATFORM, YOUR ITEMS MAY BE EXPOSED TO SITUATIONS THAT
        ARE POTENTIALLY DANGEROUS OR HARMFUL, UNSAFE OR OTHERWISE OBJECTIONABLE, AND THAT YOU USE THE PLATFORM AT YOUR OWN RISK.
      </p>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      15. NOTICE
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        FRESHr may give notice by any means of communication reasonably anticipated to notify you of
        the information provided. You agree that all notices, disclosures, and other communications that
        we provide to you electronically satisfy any legal requirement that such communications be in writing
        or be delivered in a particular manner. You agree that you have the ability to store such electronic
        communications such that they remain accessible to you in an unchanged form. By way of example only,
        such communication may be a general notice on the Platform or via email to the email address listed
        on your FRESHr account. It is your obligation to update your account information so that we may contact
        you as may be necessary. Such notice shall be deemed to have been given 48 hours after dispatch.
        If physical notice (e.g., US Mail) is used, then such notice shall be deemed to have been given 7 days after dispatch.
      </p>
      <p>
        Except as explicitly described in the Dispute Resolution and Arbitration section, you may give notice to FRESHr
        (such notice shall be deemed given when received by FRESHr) at any time by any of the following: letter delivered
        by nationally recognized overnight delivery service or first class postage prepaid mail to FRESHr at the following address:
      </p>
      <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
        <li>FRESHr Inc.</li>
        <li>P.O. Box 4237</li>
        <li>San Rafael, CA 94913-4237</li>
      </ul>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      16. ASSIGNMENT
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      You may not assign or transfer these Terms or your rights under these Terms, in whole or in
      part, by operation of law or otherwise, without our prior written consent. We may assign these
      Terms in whole or in part at any time to any entity without your notice or consent. Any purported
      assignment by you in violation of this section shall be void.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      17. TERM AND TERMINATION AGREEMENT
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      These Terms are effective until terminated by you or FRESHr as described below. Your rights
      under these Terms will terminate automatically without notice from FRESHr if you fail to comply
      with any of these Terms (including by violating any license restriction contained in these Terms).
      In addition, FRESHr may in its sole discretion terminate your user account on the Platform or
      suspend or terminate your access to the Platform at any time without notice. We also reserve the
      right to modify or discontinue the Platform at any time (including by limiting or discontinuing
      certain features of the Platform) without notice to you. We will have no liability whatsoever on
      account of any change to the Platform or any suspension or termination of your access to or use
      of the Platform. You may terminate these Terms at any time by closing your account, uninstalling
      the App, and ceasing use of the Platform. Sections 5.3, 6, 7-18, and 19, 20 survive termination
      of your account or these Terms.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      18. DISPUTE RESOLUTION AND ARBITRATION
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.1. DISPUTES
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      FRESHr is not a party to, has no involvement or interest in, makes no representations or warranties
      as to, and has no responsibility or liability with respect to any communications, transactions,
      interactions, disputes or any relations whatsoever between you and any other user, any Buyer,
      Seller or Distributor, or other third party. Disputes between you and FRESHr are subject to this
      Section 18. You and FRESHr agree that these Terms affect interstate commerce and that the Federal
      Arbitration Act governs the interpretation and enforcement of the arbitration provisions in these Terms.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.2. GENERALLY
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        In the interest of resolving Disputes between you and FRESHr in the most expedient and cost
        effective manner, you and FRESHr agree that every Dispute arising in connection with these
        Terms will be resolved by binding individual arbitration. Arbitration is more informal than a
        lawsuit in court. Arbitration uses a neutral arbitrator instead of a judge or jury, and can be
        subject to very limited review by courts. While the parties will be permitted to engage in
        discovery or exchange of non-privileged information relevant to the dispute, arbitration may allow
        for more limited discovery allowed for in court. Arbitrators can award the same damages and relief
        that a court can award. Our agreement to arbitrate disputes includes all claims arising out of or
        relating to any aspect of these Terms, the Platform, or your relationship with us, whether based in
        contract, tort, statute, fraud, misrepresentation, or any other legal theory, and regardless of
        whether a claim arises during or after the termination of these Terms.
      </p>
      <p>
        YOU UNDERSTAND AND AGREE THAT, BY ENTERING INTO THESE TERMS, YOU AND FRESHR ARE EACH WAIVING THE
        RIGHT TO GO TO COURT OR TO PARTICIPATE IN A CLASS OR REPRESENTATIVE ACTION.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.3. EXCEPTIONS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      Despite the provisions of the paragraph directly above, we both agree that nothing in these Terms
      will be deemed to waive, preclude, or otherwise limit the right of either of us to: (1) Bring an
      individual action in small claims court; (2) Pursue an enforcement action through the applicable federal,
      state, or local agency if that action is available; or (3) File suit in a court of law to address an
      intellectual property infringement claim.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.4. NO CLASS OR REPRESENTATIVE ACTIONS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        YOU AND FRESHR AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL
        CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
      </p>
      <p>
        Further, unless both you and FRESHr agree otherwise, the arbitrator may not consolidate more than one
        person’s claims, and may not otherwise preside over any form of a representative or class proceeding.
        If any court or arbitrator determines that this “No Class or Representative Actions” section is void or
        unenforceable for any reason or that an arbitration can proceed on a class or representative basis, then
        the arbitration provisions set forth above shall be deemed null and void in their entirety and the parties
        shall be deemed to have not agreed to arbitrate disputes.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.5. 30-DAY RIGHT TO OPT OUT
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      You have the right to opt out and not be bound by the arbitration and class action waiver provisions
      set forth above by submitting a request to info@freshr.com. The notice must be sent within thirty (30)
      days of your creating an account with FRESHr or the effective date of the first set of Terms containing
      a Dispute Resolution and Arbitration section, whichever is later; otherwise, you shall be bound to
      arbitrate disputes in accordance with the terms of these paragraphs. If you opt out of these arbitration
      provisions, FRESHr also will not be bound by them.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      18.6. MODIFICATIONS
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      Except for inconsequential changes that do not affect any rights or obligations herein, FRESHr
      will provide thirty (30) days' notice of any changes to this section by posting on the Platform,
      sending you a message, or otherwise notifying you when you are logged into your account. Amendments
      will become effective thirty (30) days after they are posted on the Platform or sent to you, or
      otherwise notified when you are logged into your account. Changes to this section will otherwise
      apply prospectively only to claims arising after the thirtieth (30th) day. If a court or arbitrator
      decides that this subsection on "Modifications" is not enforceable or valid, then this subsection shall
      be severed from the section entitled “Dispute Resolution and Arbitration,” and the court or arbitrator
      shall apply the first Dispute Resolution and Arbitration section in existence after you began using the
      Platform. You may otherwise reject the change by sending us written notice within thirty (30) days of the
      change to FRESHr’s address for Notice, in which case your account with FRESHr will be immediately terminated
      and this arbitration provision, as in effect immediately prior to the amendments you reject, will survive.
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      19. MODIFICATION OF THESE TERMS
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        Except as explicitly described in the Dispute Resolution and Arbitration section, we reserve
        the right to update or modify the Terms at any time without prior notice, and such changes will
        be effective immediately upon being posted through the Platform, except as set forth below. These
        Terms identify the date of last update. Except as explicitly described in the Dispute Resolution and
        Arbitration section, your use of the Platform following any such change constitutes your agreement to
        be bound by the modified Terms. In the case of material changes to these Terms, we will make reasonable
        efforts to notify you of the change, such as through sending an email to the address you may have used to
        register for an account, through a pop-up window on the Platform, or other similar mechanism.
      </p>
      <p>
        You acknowledge and agree that if FRESHr modifies any provision of these Terms (including any information
        referenced at hyperlinks), other than the Dispute Resolution and Arbitration section herein, you will not
        have a renewed opportunity to opt out of arbitration. You further acknowledge and agree that unless the
        Dispute Resolution and Arbitration section herein is materially different from any prior arbitration provision
        with FRESHr to which you may be bound, your acceptance of these Terms does not create a renewed opportunity
        to opt out of arbitration (if applicable).
      </p>
      <p>
        Except as explicitly described in the Dispute Resolution and Arbitration section, material changes to
        these Terms will be effective upon the earlier of: (1) Your first use of the Platform with actual notice
        of such change, or (2) thirty (30) days from posting of such change.
      </p>
      <p>
        Disputes arising under these Terms will be resolved in accordance with the version of the Terms in place
        at the time the dispute arose. We encourage you to review these Terms frequently to stay informed of the
        latest modifications.
      </p>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      20. CONFIDENTIALITY
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        You acknowledge and agree that when using the Platform, you may have direct or indirect
        access or exposure to FRESHr’s confidential information ("Confidential Information").
        Confidential Information includes FRESHr’s data, provider IDs, user information, delivery
        recipient information, delivery provider information, package information, and the transaction
        volume, marketing and business plans, business, financial, technical, operational and such other
        nonpublic information (whether disclosed in writing or verbally) that FRESHr designates as being
        proprietary or confidential or that you should reasonably know to treat as confidential.
      </p>
      <p>
        You acknowledge and agree that: (1) all Confidential Information shall remain the exclusive
        property of the FRESHr; (2) you shall not use Confidential Information for any purpose except
        in furtherance of your use of the Platform; (3) you shall not disclose Confidential Information
        to any third party; and (d) you shall not store or keep Confidential Information and shall return
        or destroy (with confirmation of destruction) all Confidential Information upon the termination
        of your use of the Platform or at the request of FRESHr.
      </p>
      <p>
        Notwithstanding the foregoing, Confidential Information shall not include any information to
        the extent it: (1) is or becomes part of the public domain through no action or omission by you;
        (2) was possessed by you prior to your use of the Platform without an obligation of confidentiality;
        or (3) is disclosed to you by a third party having no obligation of confidentiality with respect thereto.
      </p>
    </Typography>
    <Typography className={classes.bottomSpacing} variant="h5" color="textPrimary">
      21. GENERAL
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      21.1. GOVERNING LAW
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      <p>
        These Terms are governed by the laws of the State of California without regard to conflict of law principles.
      </p>
      <p>
        No joint venture, partnership, employment, or agency relationship exists between you, FRESHr or
        any third-party provider as a result of the Terms or use of the Platform.
      </p>
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      21.2. WAIVER AND SEVERABILITY
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      Except as explicitly described in the Dispute Resolution and Arbitration section, if any provision
      of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining
      provisions shall be enforced to the fullest extent under law. The failure of FRESHr to enforce any
      right or provision in these Terms shall not constitute a waiver of such right or provision unless
      acknowledged and agreed to by FRESHr in writing.
    </Typography>
    <Typography className={classes.bottomLeftSpacing} variant="h6" color="textPrimary">
      21.3. ENTIRE AGREEMENT
    </Typography>
    <Typography className={classes.bottomLeftLeftSpacing} variant="subtitle2" color="textSecondary">
      These Terms comprise the entire agreement between you and FRESHr and supersedes all prior or
      contemporaneous negotiations, discussions or agreements, whether written or oral, between the parties
      regarding the subject matter contained in these Terms.
    </Typography>
  </BasicMain>
);

TermsOfService.propTypes = {
  classes: PropTypes.shape({}).isRequired,
};

export default compose(
  setDisplayName('TermsOfService'),
  withStyles(styles),
  withTracker,
)(TermsOfService);
